<template>
  <v-sheet class="d-flex flex-column">
    <v-card class="rounded-lg elevation-0 table-wrap" :style="{ border: '1px solid #E0E3E2' }">
      <v-data-table
        :headers="columnsNormalized"
        :items="sortedItems"
        :items-per-page="itemsPerPageSync"
        :page="pageSync"
        class="styled-table"
        item-key="id"
        hide-default-footer
      >
        <template #headers="{ columns }">
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              class="th-head"
              :class="[
                col.align === 'end' ? 'text-end' : col.align === 'center' ? 'text-center' : 'text-start',
                col.isSortable ? 'th-head--sortable' : 'th-head--disabled',
                isSortedCol(col) ? 'th-head--sorted' : ''
              ]"
              @click="toggleSort(col)"
              @keydown.enter.prevent="toggleSort(col)"
              @keydown.space.prevent="toggleSort(col)"
              :tabindex="col.isSortable ? 0 : -1"
              :aria-sort="ariaSort(col)"
              :role="col.isSortable ? 'button' : undefined"
            >
              <slot :name="'header:'+col.key" :column="col">
                <span class="th-content">
                  <span class="th-title">{{ col.title }}</span>

                  <v-icon
                    v-if="col.isSortable"
                    size="16"
                    class="sort-icon"
                    :class="{ 'sort-icon--visible': isSortedCol(col) }"
                  >
                    {{ sortIcon(col) }}
                  </v-icon>
                </span>
              </slot>
            </th>
          </tr>
        </template>

        <template #item="{ item, index, columns }">
          <tr
            :class="['tr-body', index === highlightIndex ? 'row-highlight' : '']"
            @click="$emit('row:click', item)"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              class="td"
              :class="[
                col.align === 'end' ? 'text-end' : col.align === 'center' ? 'text-center' : 'text-start',
                col.key === 'lt60' ? 'td--force-start' : ''
              ]"
            >
              <slot :name="'cell:'+col.key" :item="item" :column="col" :value="item[col.key]">
                <template v-if="isLinkObject(item[col.key])">
                  <a
                    :href="item[col.key].url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="link"
                  >
                    {{ item[col.key].name }}
                  </a>
                </template>

                <template v-else>
                  <v-tooltip
                    :disabled="!hasManyLines(item[col.key])"
                    location="top"
                    max-width="400"
                  >
                    <template #activator="{ props }">
                      <span
                        v-bind="props"
                        class="cell-value-with-tooltip"
                        :style="{
                          justifyContent: effectiveAlign(col) === 'end' ? 'flex-end' : 
                                          effectiveAlign(col) === 'center' ? 'center' : 'flex-start'
                        }"
                      >
                        <span class="cell-value-text">
                          {{ formatTruncatedText(item[col.key]) }}
                        </span>

                        <v-icon
                          v-if="shouldShowLowPerfTooltip(col, item[col.key])"
                          size="14"
                          class="low-perf-icon"
                        >
                          mdi-alert-circle
                        </v-icon>
                      </span>
                    </template>
                    <span>{{ item[col.key] }}</span>
                  </v-tooltip>
                </template>
              </slot>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <div class="d-flex align-center justify-end ga-6 mt-8">
      <div class="d-flex align-center ga-6">
        <span class="caption text-medium-emphasis">Itens por página:</span>
        <v-select
          v-model="itemsPerPageSync"
          :items="itemsPerPageOptions"
          density="comfortable"
          variant="outlined"
          hide-details
          style="width: 88px"
          @update:model-value="(v) => $emit('update:itemsPerPage', v)"
        />
      </div>

      <div class="d-flex align-center ga-3">
        <span>Página {{ pageSync }} de {{ totalPages }}</span>
        <v-btn icon variant="text" :disabled="pageSync === 1" @click="goTo(1)">
          <v-icon>mdi-page-first</v-icon>
        </v-btn>
        <v-btn icon variant="text" :disabled="pageSync === 1" @click="goTo(pageSync - 1)">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon variant="text" :disabled="pageSync === totalPages" @click="goTo(pageSync + 1)">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn icon variant="text" :disabled="pageSync === totalPages" @click="goTo(totalPages)">
          <v-icon>mdi-page-last</v-icon>
        </v-btn>
      </div>
    </div>

    <v-sheet v-if="showTotal && resolvedSummaryItems.length" class=" d-flex flex-column justify-start align-start mt-4">
      <div class="d-flex align-center ga-2 mb-3">
        <span class="summary-title">{{ summaryTitle }}</span>
        <v-tooltip location="top" max-width="360">
          <template #activator="{ props }">
            <v-icon v-if="showSummaryInfoIcon" v-bind="props" size="14" class="summary-info">
              mdi-help-circle
            </v-icon>
          </template>
          <slot name="tooltip-content">
            {{ tooltipDescription }}
          </slot>
        </v-tooltip>
      </div>

      <v-card class="summary-card elevation-0">
        <div class="summary-row">
          <div
            v-for="(it, idx) in resolvedSummaryItems"
            :key="it.key"
            class="summary-cell"
            :class="{
              'summary-cell--last': idx === resolvedSummaryItems.length - 1,
              'summary-cell--emphasis': idx === emphasisIndexComputed
            }"
          >
            <div class="summary-label">{{ normalizeLabel(it.label) }}:</div>
            <div class="summary-value">{{ it.value }}</div>
          </div>
        </div>
      </v-card>
    </v-sheet>
  </v-sheet>
</template>

<script>
export default {
  name: 'TableInsights',

  props: {
    headers: { type: Array, required: true },
    items: { type: Array, default: () => [] },
    showTotal: { type: Boolean, default: false },
    totalTitle: { type: String, default: 'Total' },
    totals: { type: Object, default: null },
    summaryTitle: { type: String, default: 'Resumo dos dados' },
    showSummaryInfoIcon: { type: Boolean, default: true },
    summaryItems: { type: Array, default: null },
    summaryEmphasis: { type: [String, Number], default: 0 },
    totalColSpan: { type: Number, default: 1 },
    highlightIndex: { type: Number, default: -1 },
    page: { type: Number, default: 1 },
    itemsPerPage: { type: Number, default: 5 },
    itemsPerPageOptions: { type: Array, default: () => [5, 10, 25, 50] },
    tooltipDescription: { type: String, default: '' }
  },

  emits: ['update:page', 'update:itemsPerPage', 'row:click'],

  data () {
    return {
      sortKey: null,
      sortDesc: false
    }
  },

  computed: {
    sortableKeysMap () {
      const items = Array.isArray(this.items) ? this.items : []
      const keys = (this.headers || []).map(h => h.key).filter(Boolean)

      const map = {}
      keys.forEach(k => { map[k] = false })

      for (const it of items) {
        for (const k of keys) {
          if (map[k]) continue

          const v = this.getSortValue(it, k)
          const hasValue = !(v === null || v === undefined || v === '')
          if (hasValue) map[k] = true
        }
        if (keys.every(k => map[k])) break
      }

      return map
    },

    columnsNormalized () {
      return (this.headers || []).map(c => ({
        title: c.title ?? c.text ?? '',
        key: c.key,
        align: 'start',
        isSortable: c.sortable !== false && !!this.sortableKeysMap?.[c.key]
      }))
    },

    sortedItems () {
      const list = Array.isArray(this.items) ? this.items : []
      if (!this.sortKey) return list

      const key = this.sortKey
      const desc = this.sortDesc

      return list
        .map((it, idx) => ({ it, idx }))
        .sort((a, b) => {
          const va = this.getSortValue(a.it, key)
          const vb = this.getSortValue(b.it, key)

          const aNil = va === null || va === undefined || va === ''
          const bNil = vb === null || vb === undefined || vb === ''
          if (aNil && bNil) return a.idx - b.idx
          if (aNil) return 1
          if (bNil) return -1

          const na = typeof va === 'number' ? va : Number(va)
          const nb = typeof vb === 'number' ? vb : Number(vb)
          const aIsNum = Number.isFinite(na)
          const bIsNum = Number.isFinite(nb)

          let cmp = 0
          if (aIsNum && bIsNum) {
            cmp = na - nb
          } else {
            cmp = String(va).localeCompare(String(vb), 'pt-BR', {
              numeric: true,
              sensitivity: 'base'
            })
          }

          if (cmp === 0) return a.idx - b.idx
          return desc ? -cmp : cmp
        })
        .map(x => x.it)
    },

    pageSync: {
      get () { return this.page },
      set (v) { this.$emit('update:page', v) }
    },

    itemsPerPageSync: {
      get () { return this.itemsPerPage },
      set (v) { this.$emit('update:itemsPerPage', v) }
    },

    totalPages () {
      const total = this.items.length
      return Math.max(1, Math.ceil(total / this.itemsPerPageSync))
    },

    resolvedSummaryItems () {
      const totals = this.totals || {}

      if (Array.isArray(this.summaryItems) && this.summaryItems.length) {
        return this.summaryItems
          .map(si => {
            const header = this.columnsNormalized.find(h => h.key === si.key)
            const label = si.label ?? header?.title ?? si.key
            const value = si.value ?? totals?.[si.key]
            return { key: si.key, label, value }
          })
          .filter(it => it.value !== undefined && it.value !== null && String(it.value) !== '')
      }

      const fromHeaders = this.columnsNormalized
        .filter(h => Object.prototype.hasOwnProperty.call(totals, h.key))
        .map(h => ({ key: h.key, label: h.title, value: totals[h.key] }))

      const headerKeys = new Set(this.columnsNormalized.map(h => h.key))
      const extras = Object.keys(totals)
        .filter(k => !headerKeys.has(k))
        .map(k => ({ key: k, label: k, value: totals[k] }))

      return [...fromHeaders, ...extras].filter(it =>
        it.value !== undefined && it.value !== null && String(it.value) !== ''
      )
    },

    emphasisIndexComputed () {
      const v = this.summaryEmphasis
      if (typeof v === 'number') return Math.max(0, v)

      if (typeof v === 'string') {
        const idx = this.resolvedSummaryItems.findIndex(i => i.key === v)
        return idx >= 0 ? idx : 0
      }
      return 0
    }
  },

  watch: {
    totalPages(newTotal) {
      if (this.pageSync > newTotal) {
        this.goTo(newTotal);
      }
    },
    items: {
      handler() {
        if (this.pageSync !== 1) {
          this.goTo(1);
        }
      },
      deep: false
    }
  },

  methods: {
    goTo (p) {
      const clamped = Math.min(Math.max(1, p), this.totalPages)
      this.pageSync = clamped
    },

    effectiveAlign (col) {
      if (this.isLowPerformanceColumn(col)) return 'start'
      return col?.align ?? 'start'
    },

    toggleSort (col) {
      if (!col?.key || !col.isSortable) return

      if (this.sortKey !== col.key) {
        this.sortKey = col.key
        this.sortDesc = false
      } else if (this.sortDesc === false) {
        this.sortDesc = true
      } else {
        this.sortKey = null
        this.sortDesc = false
      }

      this.goTo(1)
    },

    isSortedCol (col) {
      return !!col?.key && this.sortKey === col.key
    },

    sortIcon (col) {
      if (this.sortKey !== col.key) return 'mdi-swap-vertical'
      return this.sortDesc ? 'mdi-arrow-down' : 'mdi-arrow-up'
    },

    ariaSort (col) {
      if (!col?.isSortable) return 'none'
      if (this.sortKey !== col.key) return 'none'
      return this.sortDesc ? 'descending' : 'ascending'
    },

    getSortValue (item, key) {
      const v = item?.[key]
      if (this.isLinkObject(v)) return v.name
      const p = this.toPercentNumber(v)
      return p !== null ? p : v
    },

    formatTotal (v) {
      return v ?? ''
    },

    isLinkObject (val) {
      return val &&
        typeof val === 'object' &&
        'name' in val &&
        'url' in val
    },

    normalizeLabel (label) {
      const s = String(label ?? '').trim()
      return s.replace(/:\s*$/, '')
    },

    isLowPerformanceColumn (col) {
      if (col?.key === 'lt60') return true

      const t = String(col?.title ?? '').toLowerCase()
      return t.includes('menor de 60')
    },

    toPercentNumber (raw) {
      if (raw === null || raw === undefined) return null
      if (this.isLinkObject(raw)) return null

      if (typeof raw === 'number') {
        if (raw >= 0 && raw <= 1 && !Number.isInteger(raw)) return raw * 100
        return raw
      }

      if (typeof raw === 'string') {
        const s = raw.trim()
        if (!s) return null

        const normalized = s.replace('%', '').replace(/\s+/g, '').replace(',', '.')
        const n = Number.parseFloat(normalized)
        return Number.isFinite(n) ? n : null
      }

      const n = Number(raw)
      return Number.isFinite(n) ? n : null
    },

    shouldShowLowPerfTooltip (col, value) {
      if (!this.isLowPerformanceColumn(col)) return false

      const pct = this.toPercentNumber(value)
      if (pct === null) return false

      return pct <= 15
    },
    hasManyLines (val) {
      if (typeof val !== 'string') return false
      return val.length > 37
    },

    formatTruncatedText (val) {
      if (typeof val !== 'string') return val
      if (val.length <= 37) return val
      return val.substring(0, 37) + '...'
    }
  }
}
</script>

<style scoped>
.table-wrap {
  --tbl-border:#E0E3E2;
  --tbl-header-bg:#F9FBFB;
  --row-highlight:#EAF4F4;
}

.styled-table :deep(table){
  border-collapse: collapse !important;
  width: 100%;
  table-layout: fixed;
}

.styled-table :deep(.v-data-table__wrapper),
.styled-table :deep(.v-table__wrapper){
  padding: 0 !important;
  overflow-x: auto;
}

.th-head{
  width: 100%;
  background: var(--tbl-header-bg);
  color: #5B5E5E;
  font-weight: 500;
  text-align: start;
  padding: 14px 16px;
  border-bottom: 1px solid var(--tbl-border);
  width: auto;
}
.th-head:first-child{ border-top-left-radius: 10px; }
.th-head:last-child{ border-top-right-radius: 10px; }
.th-content{
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
  width: 100%;
}

.th-head--sortable{
  cursor: pointer;
  user-select: none;
}

.th-head--disabled{
  cursor: default;
}

.sort-icon{
  color: #6F7474;
  opacity: 0;
  transition: opacity 120ms ease;
}

.th-head--sortable:hover .sort-icon{
  opacity: 1;
}

.sort-icon--visible{
  opacity: 1;
}

.td{
  padding: 14px 16px;
  text-align: start;
  border-bottom: 1px solid var(--tbl-border);
  color: #1A1D1D;
}
.td-right{ text-align: right; }

.tr-body{ background: #FFFFFF !important; cursor: pointer; }
.tr-body:hover{ background: #EFF1F0 !important; }
.row-highlight{ background: #FFFFFF !important; }

.tr-total{
  background: #F3F5F4;
  font-weight: 500;
}
.tr-total .td{ border-bottom: none; }

:deep(.v-toolbar .v-btn){ min-width: 38px; height: 38px; }
:deep(.v-toolbar .v-btn.rounded-pill){ height: 36px; }

.link {
  color: #505353;
  text-underline-position: auto;
}

.summary-title {
  color: #1A1D1D;
  font-size: 12px;
  font-weight: 600;
}

.summary-info {
  color: #6F7474;
}

.summary-card {
  display: inline-flex;
  max-width: 100%;
  border: 1px solid #E0E3E2;
  border-radius: 10px;
  overflow: hidden;
  background: #FFFFFF;
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  text-align: start;
}

.summary-cell {
  padding: 14px 16px;
  border-right: 1px solid #E0E3E2;
  min-width: 140px;
}

.summary-cell--last {
  border-right: none;
}

.summary-cell--emphasis {
  background: #F3F5F4;
}

.summary-label {
  color: #1A1D1D;
  font-size: 12px;
  font-weight: 600;
}

.summary-value {
  margin-top: 2px;
  color: #1A1D1D;
  font-size: 12px;
  font-weight: 400;
}

.cell-value-with-tooltip{
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: 100%;
}

.low-perf-icon{
  color: #F4B400;
}

.td--force-start :deep(.justify-end){
  justify-content: flex-start !important;
}
</style>